import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Save, Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { profileService } from '../services/api';

interface UserProfile {
  id: number;
  name: string;
  email: string;
  username: string;
  created_at: string;
  updated_at: string;
}

interface ProfileFormData {
  name: string;
  email: string;
  password: string;
}

const AdminProfile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    label: string;
    color: string;
  }>({ score: 0, label: '', color: '' });
  
  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    email: '',
    password: ''
  });

  // Carregar dados do perfil
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      // Tentar carregar da API primeiro
      try {
        const response = await profileService.getCurrentUser();
        if (response.success && response.data) {
          // A API retorna um array, pegar o primeiro item
          const userData = Array.isArray(response.data) ? response.data[0] : response.data;
          
          const userProfile: UserProfile = {
            id: userData.id || 1,
            name: userData.full_name || userData.name || 'Usuário',
            email: userData.email || '',
            username: userData.username || 'admin',
            created_at: userData.created_at || new Date().toISOString(),
            updated_at: userData.updated_at || new Date().toISOString()
          };
          
          setProfile(userProfile);
          setFormData({
            name: userProfile.name,
            email: userProfile.email,
            password: ''
          });
          return;
        }
      } catch (apiError) {
        console.log('API não disponível, usando dados mock');
      }
      
      // Fallback para dados mock
      const mockProfile: UserProfile = {
        id: 1,
        name: 'Luara Rezende',
        email: 'contato@luaraadvocacia.com.br',
        username: 'admin',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setProfile(mockProfile);
      setFormData({
        name: mockProfile.name,
        email: mockProfile.email,
        password: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao carregar perfil' });
    } finally {
      setLoading(false);
    }
  };

  const calculatePasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength({ score: 0, label: '', color: '' });
      return;
    }

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      noSpaces: !/\s/.test(password),
      notCommon: !['123456', 'abcdef', 'qwerty', 'password', 'admin'].some(seq => 
        password.toLowerCase().includes(seq)
      )
    };

    // Calcular score
    Object.values(checks).forEach(check => {
      if (check) score++;
    });

    // Determinar label e cor
    let label = '';
    let color = '';
    
    if (score <= 2) {
      label = 'Muito fraca';
      color = 'text-red-500';
    } else if (score <= 4) {
      label = 'Fraca';
      color = 'text-orange-500';
    } else if (score <= 6) {
      label = 'Média';
      color = 'text-yellow-500';
    } else if (score <= 8) {
      label = 'Forte';
      color = 'text-green-500';
    } else {
      label = 'Muito forte';
      color = 'text-green-600';
    }

    setPasswordStrength({ score, label, color });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calcular força da senha se for o campo de senha
    if (name === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Se a senha estiver vazia, é válida (opcional)
    if (!password || password.trim() === '') {
      return { isValid: true, errors: [] };
    }
    
    // Mínimo 8 caracteres
    if (password.length < 8) {
      errors.push('Mínimo 8 caracteres');
    }
    
    // Máximo 128 caracteres
    if (password.length > 128) {
      errors.push('Máximo 128 caracteres');
    }
    
    // Pelo menos uma letra minúscula
    if (!/[a-z]/.test(password)) {
      errors.push('Pelo menos uma letra minúscula');
    }
    
    // Pelo menos uma letra maiúscula
    if (!/[A-Z]/.test(password)) {
      errors.push('Pelo menos uma letra maiúscula');
    }
    
    // Pelo menos um número
    if (!/\d/.test(password)) {
      errors.push('Pelo menos um número');
    }
    
    // Pelo menos um caractere especial
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push('Pelo menos um caractere especial (!@#$%^&*...)');
    }
    
    // Não pode conter espaços
    if (/\s/.test(password)) {
      errors.push('Não pode conter espaços');
    }
    
    // Não pode ser sequência comum
    const commonSequences = ['123456', 'abcdef', 'qwerty', 'password', 'admin'];
    if (commonSequences.some(seq => password.toLowerCase().includes(seq))) {
      errors.push('Não pode conter sequências comuns');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      return 'Email é obrigatório';
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Email inválido';
    }

    // Se está tentando alterar senha
    if (formData.password && formData.password.trim() !== '') {
      // Validação de segurança da senha
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        return `Senha: ${passwordValidation.errors.join(', ')}`;
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      // Preparar dados para envio
      const updateData: { name: string; email: string; password?: string } = {
        name: formData.name,
        email: formData.email
      };

      // Incluir senha apenas se fornecida
      if (formData.password && formData.password.trim() !== '') {
        updateData.password = formData.password;
      }

      const updateResponse = await profileService.updateProfile(updateData);
      
      if (!updateResponse.success) {
        throw new Error(updateResponse.error || 'Erro ao atualizar perfil');
      }

      setMessage({ 
        type: 'success', 
        text: 'Perfil atualizado com sucesso!' 
      });

      // Atualizar dados locais
      if (profile) {
        setProfile({
          ...profile,
          name: formData.name,
          email: formData.email,
          updated_at: new Date().toISOString()
        });
      }

      // Limpar campo de senha
      setFormData(prev => ({
        ...prev,
        password: ''
      }));

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Erro ao atualizar perfil. Tente novamente.' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wrapper py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-slate"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 text-brand-slate hover:text-brand-accent transition-colors duration-200"
              >
                <ArrowLeft size={20} />
                <span>Voltar ao Dashboard</span>
              </button>
            </div>
            
            <h1 className="text-3xl font-bold text-brand-slate mb-2">
              Meu Perfil
            </h1>
            <p className="text-brand-ink/70">
              Gerencie suas informações pessoais e altere sua senha
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informações Básicas */}
            <div className="card card-pad">
              <h2 className="text-xl font-semibold text-brand-slate mb-6 flex items-center gap-2">
                <User size={20} />
                Informações Pessoais
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-brand-slate">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                    placeholder="Digite seu nome completo"
                    required
                  />
                </div>
                
                <div className="space-y-3">
                  <label className="text-sm font-medium text-brand-slate">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                    placeholder="Digite seu e-mail"
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="label">
                  Usuário
                </label>
                <input
                  type="text"
                  value={profile?.username || ''}
                  className="input bg-gray-100 cursor-not-allowed"
                  disabled
                />
                <p className="help">
                  O nome de usuário não pode ser alterado
                </p>
              </div>
            </div>

            {/* Alteração de Senha */}
            <div className="space-y-8">
              <div className="space-y-3">
                <label className="text-sm font-medium text-brand-slate flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Nova Senha (opcional)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent pr-10"
                    placeholder="Deixe em branco se não quiser alterar a senha"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-ink/50 hover:text-brand-ink transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-brand-ink/60">
                  Deixe em branco se não quiser alterar a senha
                </p>
                
                {/* Indicador de força da senha */}
                {formData.password && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            passwordStrength.score <= 2 ? 'bg-red-500' :
                            passwordStrength.score <= 4 ? 'bg-orange-500' :
                            passwordStrength.score <= 6 ? 'bg-yellow-500' :
                            passwordStrength.score <= 8 ? 'bg-green-500' : 'bg-green-600'
                          }`}
                          style={{ width: `${(passwordStrength.score / 7) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${passwordStrength.color}`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    
                    {/* Regras de segurança */}
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="grid grid-cols-2 gap-2">
                        <div className={`flex items-center gap-1 ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{formData.password.length >= 8 ? '✓' : '○'}</span>
                          <span>Mín. 8 caracteres</span>
                        </div>
                        <div className={`flex items-center gap-1 ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{/[a-z]/.test(formData.password) ? '✓' : '○'}</span>
                          <span>Letra minúscula</span>
                        </div>
                        <div className={`flex items-center gap-1 ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                          <span>Letra maiúscula</span>
                        </div>
                        <div className={`flex items-center gap-1 ${/\d/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{/\d/.test(formData.password) ? '✓' : '○'}</span>
                          <span>Número</span>
                        </div>
                        <div className={`flex items-center gap-1 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? '✓' : '○'}</span>
                          <span>Caractere especial</span>
                        </div>
                        <div className={`flex items-center gap-1 ${!/\s/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          <span>{!/\s/.test(formData.password) ? '✓' : '○'}</span>
                          <span>Sem espaços</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mensagens */}
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg flex items-center gap-2 ${
                  message.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}
              >
                <AlertCircle size={20} />
                {message.text}
              </motion.div>
            )}

            {/* Botões */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={18} />
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  // Voltar para o dashboard do admin
                  navigate('/admin');
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminProfile;
